import {
  useCallback,
  useState,
} from 'react';

export type FinancialStatementEntry = {
  id: string;
  importId: string | null;

  accountId: string;
  accountName: string;

  postedAt: string;

  amountCents: number;
  balanceAfterCents: number | null;

  description: string;
  reference: string | null;
  externalId: string | null;

  status:
    | 'pending'
    | 'partially_reconciled'
    | 'reconciled'
    | 'ignored';

  notes: string | null;

  matchedCents: number;
  remainingCents: number;

  createdAt: string;
  updatedAt: string;
};

export type ReconciliationCandidate = {
  targetType:
    | 'payment_settlement'
    | 'financial_ledger_entry';

  targetId: string;

  amountCents: number;
  matchedCents: number;
  remainingCents: number;

  occurredAt: string;

  description: string;
  reference: string | null;

  confidence: number;
};

export type StatementFilters = {
  accountId?: string;
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
};

export type ImportStatementInput = {
  accountId: string;

  sourceType:
    | 'manual'
    | 'csv'
    | 'ofx'
    | 'api';

  fileName?: string | null;

  periodStart?: string | null;
  periodEnd?: string | null;

  notes?: string | null;

  entries: Array<{
    postedAt: string;
    amountCents: number;
    balanceAfterCents?: number | null;
    description: string;
    reference?: string | null;
    externalId?: string | null;
  }>;
};

export function useFinancialReconciliation() {
  const [
    statementEntries,
    setStatementEntries,
  ] = useState<
    FinancialStatementEntry[]
  >([]);

  const [
    isLoadingStatementEntries,
    setIsLoadingStatementEntries,
  ] = useState(false);

  const [
    reconciliationError,
    setReconciliationError,
  ] = useState<string | null>(
    null
  );

  const loadStatementEntries =
    useCallback(
      async (
        filters:
          StatementFilters = {}
      ) => {
        setIsLoadingStatementEntries(
          true
        );

        setReconciliationError(
          null
        );

        try {
          const params =
            new URLSearchParams();

          if (filters.accountId) {
            params.set(
              'accountId',
              filters.accountId
            );
          }

          if (filters.status) {
            params.set(
              'status',
              filters.status
            );
          }

          if (filters.from) {
            params.set(
              'from',
              filters.from
            );
          }

          if (filters.to) {
            params.set(
              'to',
              filters.to
            );
          }

          params.set(
            'limit',
            String(
              filters.limit ?? 500
            )
          );

          const response =
            await fetch(
              `/api/financial-statement-entries?${params.toString()}`,
              {
                credentials:
                  'include',
              }
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.entries
            )
          ) {
            throw new Error(
              data.error ||
                `GET extrato falhou com status ${response.status}`
            );
          }

          setStatementEntries(
            data.entries
          );

          return (
            data.entries as
              FinancialStatementEntry[]
          );
        } catch (error: any) {
          setStatementEntries(
            []
          );

          setReconciliationError(
            error?.message ||
              'Não foi possível carregar a conciliação financeira.'
          );

          return [];
        } finally {
          setIsLoadingStatementEntries(
            false
          );
        }
      },
      []
    );

  const importStatementEntries =
    useCallback(
      async (
        input:
          ImportStatementInput
      ) => {
        const response =
          await fetch(
            '/api/financial-statement-entries',
            {
              method: 'POST',

              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.import
        ) {
          throw new Error(
            data.error ||
              `Importação do extrato falhou com status ${response.status}`
          );
        }

        return data.import;
      },
      []
    );

  const loadCandidates =
    useCallback(
      async (
        statementEntryId:
          string,

        mode:
          'automatic' | 'manual' =
            'automatic'
      ) => {
        const response =
          await fetch(
            `/api/financial-reconciliation/${encodeURIComponent(statementEntryId)}/candidates?mode=${encodeURIComponent(mode)}`,
            {
              credentials:
                'include',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !Array.isArray(
            data.candidates
          )
        ) {
          throw new Error(
            data.error ||
              `Busca de candidatos falhou com status ${response.status}`
          );
        }

        return (
          data.candidates as
            ReconciliationCandidate[]
        );
      },
      []
    );

  const createReconciliationMatch =
    useCallback(
      async (
        input: {
          statementEntryId:
            string;

          targetType:
            ReconciliationCandidate['targetType'];

          targetId:
            string;

          matchedAmountCents:
            number;

          matchMethod:
            'automatic'
            | 'manual';

          confidence?:
            number | null;

          notes?:
            string | null;
        }
      ) => {
        const response =
          await fetch(
            '/api/financial-reconciliation-matches',
            {
              method: 'POST',

              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.match
        ) {
          throw new Error(
            data.error ||
              `Conciliação falhou com status ${response.status}`
          );
        }

        return data;
      },
      []
    );

  return {
    statementEntries,
    isLoadingStatementEntries,
    reconciliationError,

    loadStatementEntries,
    importStatementEntries,
    loadCandidates,
    createReconciliationMatch,
  };
}